// Multi-location stock types (frontend mirror of functions/src/services/stock-model.ts)
//
// Stock truth lives per variant per location in `stock_levels`; the familiar
// `product.sizes[].stockCount` remains as a derived rollup for storefront
// reads. All stock WRITES go through Cloud Functions — the UI never mutates
// these collections directly.

export type LocationKind = 'OWN' | 'THIRD_PARTY_3PL' | 'RETAIL_CONSIGNMENT' | 'SHOPIFY_MIRROR';

/**
 * The one external system allowed to report physical stock at a location.
 * Two writers on one shelf double-count every sale, so the first external
 * reporter claims it and the rest are refused.
 */
export type WriterSource = 'shopify' | 'partner_api' | 'square' | 'stripe_external';

export const WRITER_SOURCE_LABELS: Record<WriterSource, string> = {
  shopify: 'Shopify',
  partner_api: 'Partner API',
  square: 'Square',
  stripe_external: 'Your Stripe account',
};

export type StockChannel =
  | 'FASH'
  | 'SHOPIFY_ONLINE'
  | 'SHOPIFY_POS'
  | 'RETAIL_PARTNER'
  | 'WHOLESALE'
  | 'INTERNAL';

export interface StockLocationAddress {
  street: string;
  city: string;
  state: string;
  zipCode: string;
  lat: number | null;
  lng: number | null;
}

/** stock_locations/{locationId} */
export interface StockLocation {
  locationId: string;
  businessId: string;
  name: string;
  kind: LocationKind;
  partnerId: string | null;
  address: StockLocationAddress | null;
  /** Inside a FASH delivery zone — required before FASH can sell from here. */
  deliveryZoneEligible: boolean;
  /** Merchant switch: may FASH sell units sitting at this location. */
  sellableOnFash: boolean;
  /** Units held back from FASH at this location (safety buffer). */
  safetyBuffer: number;
  /** True when FASH can place real holds here (Shopify / integrated 3PL). */
  canEnforceHold: boolean;
  isDefault: boolean;
  active: boolean;
  /** Null while unclaimed; set by whichever external system reported first. */
  writerSource: WriterSource | null;
  externalRefs: {
    shopifyLocationId: number | null;
    squareLocationId?: string | null;
  };
  createdAt: any;
  updatedAt: any;
}

/** stock_levels/{locationId}__{variantId} */
export interface StockLevel {
  businessId: string;
  locationId: string;
  productId: string;
  variantId: string;
  sizeLabel: string;
  canonicalSku: string;
  /** Physical units at this location. */
  onHand: number;
  /** Units held for in-flight FASH checkouts. */
  reservedByFash: number;
  /** Units en route via an open transfer. */
  inTransit: number;
  discrepancy: boolean;
  archived: boolean;
  externalRefs: {
    shopifyInventoryItemId: number | null;
    shopifyVariantId: number | null;
    partnerSku: string | null;
    squareCatalogObjectId?: string | null;
  };
  lastSourceUpdatedAt: any;
  lastMovementAt: any;
  updatedAt: any;
}

/** Sellable-on-FASH units a level contributes (pool minus buffer model). */
export function levelFashSellable(
  level: Pick<StockLevel, 'onHand' | 'reservedByFash'>,
  buffer: number,
): number {
  return Math.max(0, level.onHand - buffer) - level.reservedByFash;
}

/** partners/{partnerId} */
export interface Partner {
  partnerId: string;
  businessId: string;
  name: string;
  kind: 'THIRD_PARTY_3PL' | 'RETAIL_CONSIGNMENT';
  contactEmail: string | null;
  /** Locations this partner may report against. */
  locationIds: string[];
  active: boolean;
  createdAt: any;
}

/** stock_transfers/{transferId} */
export interface StockTransfer {
  transferId: string;
  businessId: string;
  fromLocationId: string;
  toLocationId: string;
  items: Array<{ variantId: string; productId: string; sizeLabel: string; quantity: number }>;
  status: 'in_transit' | 'received' | 'cancelled';
  createdBy: string;
  createdAt: any;
  receivedAt: any | null;
}

// ── Non-Shopify inventory sources ───────────────────────────────────────────

/**
 * payment_signals/{signalId} — money moved on a channel FASH does not own.
 *
 * A signal is evidence, never an instruction. FASH only decrements when the
 * payment named a SKU it already knows; an amount-only charge is recorded and
 * surfaced for review instead of guessed at.
 */
export interface PaymentSignal {
  signalId: string;
  businessId: string;
  provider: 'square' | 'stripe_external';
  externalEventId: string;
  externalOrderId: string | null;
  amountCents: number;
  currency: string;
  lines: Array<{
    sku: string | null;
    externalCatalogId: string | null;
    quantity: number;
    variantId: string | null;
    applied: boolean;
  }>;
  status: 'matched' | 'partial' | 'unmatched' | 'reviewed';
  /** Set by the detector when nothing explained this sale in time. */
  needsReview: boolean;
  expectMovement: boolean;
  locationId: string | null;
  note: string | null;
  occurredAt: number | null;
  createdAt: number | null;
  reviewedAt: number | null;
  reviewedBy: string | null;
}

export type SquareInventoryMode = 'counts' | 'orders' | 'off';

export interface SquareStatus {
  connected: boolean;
  merchantId?: string | null;
  environment?: 'production' | 'sandbox';
  inventoryMode?: SquareInventoryMode;
  connectedAt?: number | null;
  lastEventAt?: number | null;
  webhookUrl?: string;
  mappedLocations?: Array<{
    locationId: string;
    name: string;
    squareLocationId: string | null;
    deliveryZoneEligible: boolean;
    sellableOnFash: boolean;
    safetyBuffer: number;
  }>;
}

export interface ExternalStripeStatus {
  connected: boolean;
  locationId?: string;
  applyMovements?: boolean;
  keyKind?: 'restricted' | 'secret';
  webhookUrl?: string;
  lastEventAt?: number | null;
}

/** One row of a pasted / uploaded catalog, after server-side planning. */
export interface PlannedCatalogRow {
  sku: string;
  productName: string;
  sizeLabel: string;
  quantity: number;
  price: number | null;
  category: string | null;
  imageUrl: string | null;
  action: 'create_product' | 'add_size' | 'update_stock' | 'remap_sku';
  productId: string | null;
  variantId: string | null;
  currentQuantity: number | null;
  note: string | null;
}

export interface CatalogPreview {
  locationId: string;
  errors: string[];
  unmappedHeaders: string[];
  rows: PlannedCatalogRow[];
  summary: {
    total: number;
    createProducts: number;
    addSizes: number;
    updateStock: number;
    remapSku: number;
  };
}

/** abandonment_signals/{shopperId} — admin-only evidence trail */
export interface AbandonmentSignal {
  shopperId: string;
  /** Rolling window of abandoned-checkout events. */
  events: Array<{
    orderId: string;
    businessId: string;
    variantIds: string[];
    at: any;
  }>;
  abandoned24h: number;
  flagged: boolean;
  lastSignalAt: any;
  lastNotifiedAt: any | null;
}
